// src/hooks/useChatStream.ts

import React from 'react';
import { buildApiUrl, getApiHeaders } from '../support/queriesHelpers';
import {
  ChatMessage,
  ChatSendPayload,
  McpToolTrace,
  StreamEvent,
} from '../types/aiTypes';

type UiMsg = {
  role: 'user' | 'assistant';
  content: string;
};

type SendOptions = {
  payload: ChatSendPayload;
  onUserMessage?: (message: ChatMessage) => void;
  onAssistantToken?: (token: string) => void;
  onAssistantMessage?: (message: ChatMessage) => void;
  onStatus?: (status: string) => void;
  onToolTrace?: (trace: McpToolTrace) => void;
  onDone?: () => void;
};

type LegacySendOptions = {
  sessionId: string;
  userText: string;
  useDrift: boolean;
  setUiMessages: React.Dispatch<React.SetStateAction<UiMsg[]>>;
  refetchHistory?: () => any;
  pipelineId?: string;
  useMcp?: boolean;
  mcpServerIds?: Array<string | number>;
  mcpCapabilityNames?: string[];
  forceMcpRefresh?: boolean;
  related_type?: string;
  related_id?: string | number;
  user_id?: string | number;
  /** Optional current app path, forwarded to the backend as grounding context. */
  path?: string;
  setToolTraces?: React.Dispatch<React.SetStateAction<McpToolTrace[]>>;
};

function normalizeToolStatus(status: unknown): McpToolTrace['status'] {
  const value = String(status || '').toLowerCase();

  if (value === 'completed' || value === 'success' || value === 'ok') return 'success';
  if (value === 'error' || value === 'failed' || value === 'denied') return 'error';
  if (value === 'started' || value === 'running') return 'running';

  return value || 'running';
}

// Fallback for frames that carry no `event:` name (e.g. bare `data: {...}` from
// other backends/endpoints). Shape-sniffs the JSON the way the original parser
// did, but without the "coerce anything to a token" trap.
function interpretBareData(parsed: any, raw: string): StreamEvent | null {
  if (parsed?.type) return parsed as StreamEvent;

  if (parsed?.token) return { type: 'token', content: String(parsed.token) };
  if (parsed?.content) return { type: 'token', content: String(parsed.content) };
  if (parsed?.text !== undefined) return { type: 'token', content: String(parsed.text) };
  if (parsed?.response && typeof parsed.response === 'string') {
    return { type: 'token', content: parsed.response };
  }
  if (parsed?.message && typeof parsed.message === 'string') {
    return { type: 'status', message: parsed.message };
  }
  if (parsed && typeof parsed === 'object' && 'ok' in parsed && !('type' in parsed)) {
    return { type: 'status', message: '' };
  }

  // Unknown object without a recognizable shape: drop it rather than rendering
  // raw JSON as visible assistant text.
  return typeof parsed === 'string' ? { type: 'token', content: parsed } : null;
}

// Map a parsed SSE frame to a StreamEvent using the `event:` name as the source
// of truth. The name disambiguates frames whose JSON payloads look alike (e.g.
// `final` carries a `response` string with no `type`, which would otherwise be
// misread as a plain token and lose its metadata/artifacts/citations).
function interpretEvent(eventName: string, data: any, raw: string): StreamEvent | null {
  switch (eventName) {
    case 'final':
      return {
        type: 'final',
        response: String(data?.response ?? ''),
        metadata: data?.metadata ?? undefined,
        session_id: data?.session_id ?? undefined,
        conversation_id: data?.conversation_id ?? undefined,
        model: data?.model ?? undefined,
        created_at: data?.created_at ?? undefined,
      };
    case 'token':
      return { type: 'token', content: String(data?.text ?? data?.token ?? data?.content ?? '') };
    case 'status':
      return { type: 'status', message: String(data?.message ?? '') };
    case 'message':
      return { type: 'message', message: (data?.message ?? data) as ChatMessage };
    case 'tool_start':
      return { type: 'tool_start', trace: (data?.trace ?? data) as McpToolTrace };
    case 'tool_result':
      return { type: 'tool_result', trace: (data?.trace ?? data) as McpToolTrace };
    case 'tool_progress':
      // Backend agentic loop emits {tool_name, status}; surface as a tool trace.
      return {
        type: 'tool_result',
        trace: {
          capability_name: data?.tool_name || data?.capability_name || 'tool',
          status: normalizeToolStatus(data?.status),
        },
      };
    case 'error':
      return { type: 'error', message: String(data?.message ?? data?.error ?? 'Stream error') };
    case 'done':
      return { type: 'done', session_id: data?.session_id };
    case 'heartbeat':
    case 'ping':
      return null;
    case '':
      return interpretBareData(data, raw);
    default:
      // Unknown named event: honor an explicit `type`, else ignore it.
      return data?.type ? (data as StreamEvent) : null;
  }
}

// Parse one SSE frame (the block between blank-line separators). A frame may
// carry an `event:` line and one or more `data:` lines per the SSE spec.
function parseSseFrame(frame: string): StreamEvent | null {
  let eventName = '';
  const dataLines: string[] = [];

  for (const rawLine of frame.split('\n')) {
    const line = rawLine.replace(/\r$/, '');

    if (line.startsWith('event:')) {
      eventName = line.slice('event:'.length).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice('data:'.length).replace(/^ /, ''));
    }
    // ":"-comments and blank lines are ignored.
  }

  if (dataLines.length === 0) return null;

  const rawData = dataLines.join('\n');

  if (!rawData || rawData === '[DONE]') return { type: 'done' };

  let data: any;
  try {
    data = JSON.parse(rawData);
  } catch {
    // Non-JSON payload: only meaningful as streamed text.
    if (eventName === 'token' || eventName === '') {
      return { type: 'token', content: rawData };
    }
    return null;
  }

  return interpretEvent(eventName, data, rawData);
}

export function useChatStream(endpoint = 'ai/chat/stream/') {
  const abortRef = React.useRef<AbortController | null>(null);
  const [sending, setSending] = React.useState(false);
  const [streamStatus, setStreamStatus] = React.useState<string | null>(null);

  const cancel = React.useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setSending(false);
    setStreamStatus('Stopped');
  }, []);

  const send = React.useCallback(
    async ({
             payload,
             onUserMessage,
             onAssistantToken,
             onAssistantMessage,
             onStatus,
             onToolTrace,
             onDone,
           }: SendOptions) => {
      if (sending) return;

      const controller = new AbortController();
      abortRef.current = controller;

      setSending(true);
      setStreamStatus('Sending...');

      onUserMessage?.({
        role: 'user',
        content: payload.message,
        session_id: payload.session_id,
        created_at: new Date().toISOString(),
      });

      const resp = await fetch(buildApiUrl(endpoint), {
        method: 'POST',
        headers: getApiHeaders(null, 'POST', endpoint),
        body: JSON.stringify(payload),
        credentials: 'include',
        signal: controller.signal,
      });

      if (!resp.ok) {
        let detail = 'Chat request failed';

        try {
          const data = await resp.json();

          if (Array.isArray(data?.detail)) {
            detail = data.detail
              .map((d: any) => {
                const field = Array.isArray(d?.loc) ? d.loc.filter((p: any) => p !== 'body').join('.') : null;
                return field ? `${field}: ${d?.msg || 'invalid value'}` : (d?.msg || 'invalid value');
              })
              .join('; ') || detail;
          } else {
            detail = data?.error || data?.detail || data?.message || detail;
          }
        } catch {
          // no-op
        }

        setSending(false);
        setStreamStatus(null);
        throw new Error(detail);
      }

      if (!resp.body) {
        setSending(false);
        setStreamStatus(null);
        throw new Error('Streaming response body is empty');
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      const dispatch = (event: StreamEvent | null) => {
        if (!event) return;

        if (event.type === 'status') {
          setStreamStatus(event.message);
          onStatus?.(event.message);
        }

        if (event.type === 'token') {
          setStreamStatus('Receiving...');
          onAssistantToken?.(event.content);
        }

        if (event.type === 'message') {
          onAssistantMessage?.(event.message);
        }

        if (event.type === 'final') {
          // Authoritative answer: replaces any streamed placeholder and carries
          // metadata (artifacts, citations) the token stream does not.
          onAssistantMessage?.({
            role: 'assistant',
            content: event.response,
            metadata: event.metadata,
            session_id: event.session_id,
            conversation_id: event.conversation_id,
            created_at: event.created_at,
          });
        }

        if (event.type === 'tool_start' || event.type === 'tool_result') {
          onToolTrace?.(event.trace);
        }

        if (event.type === 'error') {
          throw new Error(event.message);
        }

        if (event.type === 'done') {
          onDone?.();
        }
      };

      try {
        for (;;) {
          const { value, done } = await reader.read();

          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // SSE frames are separated by a blank line ("\n\n").
          const frames = buffer.split('\n\n');
          buffer = frames.pop() || '';

          for (const frame of frames) {
            dispatch(parseSseFrame(frame));
          }
        }

        // Flush any trailing frame left without a terminating blank line.
        if (buffer.trim()) {
          dispatch(parseSseFrame(buffer));
        }

        setStreamStatus('Complete');
        onDone?.();
      } catch (error: any) {
        if (error?.name !== 'AbortError') {
          setStreamStatus('Error');
          throw error;
        }
      } finally {
        abortRef.current = null;
        setSending(false);
        setTimeout(() => setStreamStatus(null), 1200);
      }
    },
    [endpoint, sending]
  );

  const sendMessageStream = React.useCallback(
    async (opts: LegacySendOptions) => {
      let assistantStarted = false;

      await send({
        payload: {
          session_id: opts.sessionId,
          message: opts.userText,
          retrieval: {
            drift: opts.useDrift,
            pipeline_id: opts.pipelineId || undefined,
          },
          mcp: {
            enabled: opts.useMcp ?? true,
            server_ids: opts.mcpServerIds || [],
            capability_names: opts.mcpCapabilityNames || [],
            force_refresh: opts.forceMcpRefresh || false,
          },
          related_type: opts.related_type,
          related_id: opts.related_id,
          user_id: opts.user_id,
          path: opts.path,
        },
        onUserMessage: () => {
          opts.setUiMessages((prev) => [
            ...prev,
            {
              role: 'user',
              content: opts.userText,
            },
          ]);
        },
        onAssistantToken: (token) => {
          opts.setUiMessages((prev) => {
            const last = prev[prev.length - 1];

            if (assistantStarted && last?.role === 'assistant') {
              return [
                ...prev.slice(0, -1),
                {
                  ...last,
                  content: `${last.content}${token}`,
                },
              ];
            }

            assistantStarted = true;

            return [
              ...prev,
              {
                role: 'assistant',
                content: token,
              },
            ];
          });
        },
        onAssistantMessage: (message) => {
          opts.setUiMessages((prev) => {
            const last = prev[prev.length - 1];

            if (assistantStarted && last?.role === 'assistant') {
              return [
                ...prev.slice(0, -1),
                {
                  role: 'assistant',
                  content: message.content,
                },
              ];
            }

            return [
              ...prev,
              {
                role: 'assistant',
                content: message.content,
              },
            ];
          });
        },
        onToolTrace: (trace) => {
          opts.setToolTraces?.((prev) => [...prev, trace]);
        },
        onDone: () => {
          opts.refetchHistory?.();
        },
      });
    },
    [send]
  );

  React.useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  return {
    streamStatus,
    sending,
    send,
    sendMessageStream,
    cancel,
  };
}